import { useCallback, useEffect, useRef } from "react";
import { validateEmail } from "@baluu/ui/components/checkout/validation";
import {
  createBasketHolds,
  RELEASE_REASON,
  reservationEmail,
  startReservationHeartbeat,
  type BasketHolds,
} from "@baluu/ui/lib/checkout/basket-reservations";
import { releaseBasket, reserveBasket } from "~/widgets/lib/basket-reservations";
import type {
  BasketItem,
  BasketValidationResult,
} from "~/widgets/stores/basket-store";

/**
 * Seat holds while the basket overlay is open — v1's checkout semantics
 * (apps/widget useBasketReservations), on the shared lifecycle in
 * `@baluu/ui/lib/checkout/basket-reservations`:
 *
 *   - a school with `basket.reservationsEnabled` holds the seats once the
 *     buyer's email is valid and has settled, and again under a corrected
 *     email (the old one's holds released first);
 *   - while the overlay is open, a heartbeat re-validates with the email
 *     every 30s, which keeps the holds alive and the verdicts fresh — paused
 *     on a hidden tab, mid-submit and once the order is placed;
 *   - closing the overlay releases what it reserved, unless the order was
 *     placed (fulfilment consumes the holds), and removing an item releases
 *     that item's holds.
 *
 * It lives in the overlay, not in `<baluu-basket>`: several surfaces open the
 * overlay on a page with no basket element.
 */
export function useBasketHolds({
  apiUrl,
  schoolId,
  items,
  buyerEmail,
  reservationsEnabled,
  userId,
  isSuccess,
  isPaused,
  heartbeat,
  beginApply,
}: {
  apiUrl: string;
  schoolId: string;
  items: BasketItem[];
  /** The buyer form's email, as typed. */
  buyerEmail: string;
  reservationsEnabled: boolean;
  userId?: string;
  isSuccess: boolean;
  /** True while a heartbeat must not run (mid-submit, settling a return). */
  isPaused: () => boolean;
  /** One heartbeat: a validate carrying the buyer's email. */
  heartbeat: (email: string) => void;
  /** Called as a reserve starts; returns how to stamp its per-item verdicts
   *  onto the basket when it lands (the caller may drop a superseded one). */
  beginApply: () => (results: BasketValidationResult[]) => void;
}) {
  // Read at call time: a reserve fires after a debounce, and the heartbeat
  // runs for as long as the overlay is open.
  const latest = useRef({ items, userId, beginApply, heartbeat, isPaused });
  useEffect(() => {
    latest.current = { items, userId, beginApply, heartbeat, isPaused };
  });

  const holdsRef = useRef<BasketHolds | null>(null);
  useEffect(() => {
    const holds = createBasketHolds({
      reserve: async (email) => {
        const apply = latest.current.beginApply();
        try {
          const outcome = await reserveBasket({
            apiUrl,
            schoolId,
            items: latest.current.items,
            buyerEmail: email,
            userId: latest.current.userId,
          });
          if (outcome.results) apply(outcome.results);
        } catch {
          // Non-blocking: checkout-session creation reserves again before
          // Stripe, so a failed hold here never strands the buyer.
        }
      },
      release: (email) =>
        releaseBasket({
          apiUrl,
          schoolId,
          buyerEmail: email,
          reason: RELEASE_REASON.leftCheckout,
        }),
    });
    holdsRef.current = holds;
    return () => {
      holds.dispose();
      holdsRef.current = null;
    };
  }, [apiUrl, schoolId]);

  const itemCount = items.length;
  useEffect(() => {
    // An emptied basket keeps its holds until the overlay closes: the items
    // went one by one (each released) or the order was placed.
    if (itemCount === 0) return;
    holdsRef.current?.update(reservationEmail(buyerEmail, reservationsEnabled));
    // apiUrl/schoolId: a rebuilt instance (effect above) must reserve too.
  }, [buyerEmail, reservationsEnabled, itemCount, apiUrl, schoolId]);

  useEffect(() => {
    if (isSuccess) holdsRef.current?.settle();
  }, [isSuccess]);

  const heartbeatEmail = buyerEmail.trim();
  const canBeat =
    !isSuccess && itemCount > 0 && validateEmail(heartbeatEmail).isValid;
  useEffect(() => {
    if (!canBeat) return;
    return startReservationHeartbeat({
      tick: () => {
        if (latest.current.isPaused()) return;
        latest.current.heartbeat(heartbeatEmail);
      },
    });
  }, [canBeat, heartbeatEmail]);

  /** Free one item's holds; call as the item is removed. */
  const releaseItem = useCallback(
    (itemId: string) => {
      void releaseBasket({
        apiUrl,
        schoolId,
        basketItemIds: [itemId],
        reason: RELEASE_REASON.itemRemoved,
      });
    },
    [apiUrl, schoolId],
  );

  return { releaseItem };
}
